// filenameSuggestions.js
import { Anthropic } from "@anthropic-ai/sdk";
import { decryptData } from "./encryptionUtils";
import apiUsageTracker from "./apiUsageTracker";

/**
 * Extracts filename from code comments
 * Many code samples start with a comment containing a filename suggestion
 * 
 * @param {string} code - The code to parse
 * @param {string} language - The language of the code
 * @returns {string|null} - Suggested filename or null if not found
 */
export const extractFilenameFromComments = (code, language) => {
  if (!code || !language) return null;
  
  // Language-specific comment patterns
  const commentPatterns = {
    javascript: ['//', '/*'],
    typescript: ['//', '/*'],
    python: ['#'],
    ruby: ['#'],
    php: ['//', '/*', '#'],
    java: ['//', '/*'],
    csharp: ['//', '/*'],
    swift: ['//', '/*'],
    go: ['//', '/*'],
    rust: ['//', '/*'],
    cpp: ['//', '/*'],
    c: ['//', '/*'],
    css: ['/*'],
    html: ['<!--'],
    sql: ['--', '/*'],
    bash: ['#'],
    powershell: ['#'],
    r: ['#'],
    perl: ['#'],
    haskell: ['--'],
  };
  
  // Default to JavaScript comments if language not recognized
  const commentStyles = commentPatterns[language.toLowerCase()] || ['//', '/*', '#'];
  
  // Get first few lines of code
  const lines = code.split('\n').slice(0, 5);
  
  // Check for patterns like "filename: x.js" or "file: x.js" or just a standalone filename path
  const filenameRegexes = [
    /(?:file(?:name)?:?)\s+['"]?([^'"]+\.[a-zA-Z0-9]+)['"]?/i, // filename: x.js or file: x.js
    /['"]?([^'"]+\.[a-zA-Z0-9]+)['"]?/i, // Any word ending with extension
  ];
  
  // Check first few lines for comments containing filename
  for (const line of lines) {
    const trimmedLine = line.trim();
    
    // Check if line is a comment
    const isComment = commentStyles.some(style => trimmedLine.startsWith(style));
    
    if (isComment) {
      // Try each regex pattern
      for (const regex of filenameRegexes) {
        const match = trimmedLine.match(regex);
        if (match && match[1]) {
          return match[1].trim();
        }
      }
    }
  }
  
  return null;
};

/**
 * Gets the API key with trial support
 * Checks for free trial first, then falls back to stored API keys
 * 
 * @returns {string} - The API key to use or empty string if none found
 */
const getApiKey = () => {
  console.log("filenameSuggestions: getApiKey called, checking for trial first");
  
  // Check for free trial
  try {
    const FreeTrialManager = require('./components/FreeTrialManager').default;
    if (FreeTrialManager.isTrialActive()) {
      const trialKey = FreeTrialManager.getTrialApiKey();
      console.log("filenameSuggestions: Free trial is active, using trial API key");
      if (trialKey) return trialKey;
    }
  } catch (err) {
    console.error("filenameSuggestions: Error checking for trial key:", err);
  }
  
  console.log("filenameSuggestions: No trial key, checking for user API key");
  // Continue with normal key handling
  const encryptedKey = localStorage.getItem('anthropic_api_key_encrypted');
  const plainKey = localStorage.getItem('anthropic_api_key');
  
  if (encryptedKey) {
    try {
      const decrypted = decryptData(encryptedKey);
      if (decrypted && decrypted.trim() !== '') {
        return decrypted;
      }
    } catch (err) {
      console.error("Error decrypting key:", err);
      // Fall back to plain key if available
    }
  }
  
  return plainKey || '';
};

/**
 * Suggest a filename based on code content and conversation prompt
 * Uses Claude API to generate a suggestion if needed
 * 
 * @param {string} code - The code to analyze
 * @param {string} language - The language of the code
 * @param {string} prompt - The user prompt that generated the code
 * @returns {Promise<string>} - Suggested filename
 */
export const suggestFilename = async (code, language, promptContext = '') => {
  // First try to extract filename from code comments
  const extractedFilename = extractFilenameFromComments(code, language);
  if (extractedFilename) {
    return extractedFilename;
  }
  
  // Default extensions by language
  const defaultExtensions = {
    javascript: 'js',
    typescript: 'ts',
    python: 'py',
    ruby: 'rb',
    php: 'php',
    java: 'java',
    csharp: 'cs',
    swift: 'swift',
    go: 'go',
    rust: 'rs',
    cpp: 'cpp',
    c: 'c',
    css: 'css',
    html: 'html',
    sql: 'sql',
    bash: 'sh',
    powershell: 'ps1',
    r: 'r',
    perl: 'pl',
    haskell: 'hs',
  };
  
  // If we can't extract a filename, use AI to suggest one
  try {
    // Get API key using the updated method that supports trial keys
    const apiKey = getApiKey();
    
    if (!apiKey) {
      throw new Error("API key is required for filename suggestion");
    }
    
    // Create a temporary client
    const client = new Anthropic({
      apiKey: apiKey,
      dangerouslyAllowBrowser: true,
    });
    
    // Prepare code sample for the prompt
    // Limit code sample to reduce token usage
    const maxCodeLength = 1000;
    const codeSample = code.length > maxCodeLength ? 
      code.substring(0, maxCodeLength) + '...' : 
      code;
    
    // Always use Claude 3.5 Haiku for filename suggestions
    const model = 'claude-3-5-haiku-20241022';
    
    // Call the API
    const response = await client.messages.create({
      model: model,
      max_tokens: 30,
      temperature: 0.2,
      messages: [{
        role: 'user',
        content: `Given this code snippet in ${language} and the prompt that generated it, suggest a descriptive filename with appropriate extension. Return ONLY the filename, nothing else.

Prompt: ${promptContext || "No prompt available"}

Code snippet:
\`\`\`${language}
${codeSample}
\`\`\`

Suggested filename:`
      }]
    });
    
    // Track API call
    apiUsageTracker.trackApiCall(response);
    
    // Extract the suggestion
    if (response?.content && response.content[0]?.text) {
      // Clean up the response to ensure it's just a filename
      const suggestion = response.content[0].text.trim()
        .replace(/^['"]+|['"]+$/g, '') // Remove quotes
        .replace(/filename:?\s*/i, '') // Remove "filename:" prefix
        .replace(/^\s*[\r\n]+|[\r\n]+\s*$/g, ''); // Remove leading/trailing newlines
      
      // Check if the suggestion has an extension
      if (suggestion.includes('.')) {
        return suggestion;
      } else {
        // Add default extension if missing
        const ext = defaultExtensions[language.toLowerCase()] || 'txt';
        return `${suggestion}.${ext}`;
      }
    }
  } catch (error) {
    console.error("Error suggesting filename with Claude:", error);
    apiUsageTracker.trackApiError(error);
  }
  
  // Fallback to a generic filename with appropriate extension
  const ext = defaultExtensions[language.toLowerCase()] || 'txt';
  return `code.${ext}`;
};

export default {
  extractFilenameFromComments,
  suggestFilename
};